import React from 'react';
import { FaChevronDown, FaClipboardList, FaPhone, FaFileAlt, FaCalendarAlt, FaBriefcase, FaClock } from 'react-icons/fa';
import './OfficeInfo.css';
import './NewPatients.css';

const NewPatients = () => {
    const newPatientSteps = [
        {
            step: 1,
            title: "Call Our Office",
            description: "Contact our office at (781) 871-5030 to schedule your new patient appointment. Our friendly staff will help you find a convenient time and answer any initial questions you may have.",
            icon: <FaPhone />
        },
        {
            step: 2,
            title: "Gather Required Information",
            description: "Have your insurance information ready along with personal demographics including your current address, date of birth, emergency contact information, and any relevant medical history.",
            icon: <FaFileAlt />
        },
        {
            step: 3,
            title: "Schedule New Patient Appointment",
            description: "Book your introductory meeting with Dr. Kennedy. This comprehensive visit allows us to get to know you, discuss your health goals, and establish a personalized care plan.",
            icon: <FaCalendarAlt />
        },
        {
            step: 4,
            title: "Change Your Primary Care Provider",
            description: "Contact your insurance company to change your Primary Care Provider (PCP) to Dr. Kennedy. This ensures your visits are covered under your plan and establishes her as your main healthcare provider.",
            icon: <FaClipboardList />
        },
        {
            step: 5,
            title: "Prepare for Your Visit",
            description: "Bring your insurance cards, driver's license or photo ID, and any relevant medical records from previous providers. Arrive 15 minutes early to complete any remaining paperwork.",
            icon: <FaBriefcase />
        }
    ];

    return (
        <div className="new-patients-section">
            {/* Hero Section */}
            <div className="new-patients-hero">
                <div className="hero-container">
                    <div className="hero-card">
                        <div className="hero-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
                                <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor"/>
                                <path d="M5 15L6.09 18.26L10 19L6.09 19.74L5 23L3.91 19.74L0 19L3.91 18.26L5 15Z" fill="currentColor"/>
                            </svg>
                        </div>
                        
                        <h1 className="hero-title">Become a New Patient</h1>
                        
                        <p className="hero-description">
                            We're accepting new patients and would love to be your healthcare partner. 
                            Follow these simple steps to join our practice and begin receiving personalized, 
                            comprehensive primary care with Dr. Kennedy and our compassionate team.
                        </p>
                    </div>
                    
                    {/* Scroll Down Button */}
                    <div className="scroll-down-container">
                        <button 
                            className="scroll-down-btn"
                            onClick={() => {
                                const nextSection = document.querySelector('.section-header');
                                if (nextSection) {
                                    nextSection.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                            aria-label="Scroll down for more information"
                        >
                            <span className="scroll-text">Scroll for more information</span>
                            <div className="chevron-container">
                                <FaChevronDown className="scroll-chevron" />
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="new-patients-content">
                <div className="section-header">
                    <span>Getting Started</span>
                    <h2>Steps to Becoming a New Patient</h2>
                    <p className="section-description">
                        Starting care with a new healthcare provider should be simple and stress-free. 
                        Follow these easy steps to join our practice and begin receiving personalized, 
                        comprehensive primary care.
                    </p>
                </div>

                <div className="steps-container">
                    {newPatientSteps.map((step, index) => (
                        <div key={index} className="step-card">
                            <div className="step-number">
                                <span className="step-icon">{step.icon}</span>
                                <div className="step-counter">{step.step}</div>
                            </div>
                            <div className="step-content">
                                <h3 className="step-title">{step.title}</h3>
                                <p className="step-description">{step.description}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Contact Information */}
                <div className="contact-section">
                    <div className="contact-card">
                        <h3>Ready to Get Started?</h3>
                        <p>Contact our office today to begin your journey to better health</p>
                        <div className="contact-details">
                            <div className="contact-item">
                                <span className="contact-icon">
                                    <FaPhone />
                                </span>
                                <div>
                                    <strong>Phone</strong>
                                    <p><a href="tel:7818715030">(781) 871-5030</a></p>
                                </div>
                            </div>
                            <div className="contact-item">
                                <span className="contact-icon">
                                    <FaClock />
                                </span>
                                <div>
                                    <strong>Office Hours</strong>
                                    <p>Monday & Friday: 9:00 AM - 6:00 PM<br/>
                                    Tuesday - Thursday: 9:00 AM - 5:00 PM</p>
                                </div>
                            </div>
                        </div>
                        <div className="cta-buttons">
                            <a href="tel:7818715030" className="cta-button primary">
                                Call Now
                            </a>
                            <a href="/Contact" className="cta-button secondary">
                                View Locations
                            </a>
                        </div>
                    </div>
                </div>

                {/* Additional Information */}
                <div className="info-section">
                    <div className="info-grid">
                        <div className="info-card">
                            <h4>What to Expect</h4>
                            <p>Your first visit will be comprehensive, allowing Dr. Kennedy to understand your health history, current concerns, and wellness goals.</p>
                        </div>
                        <div className="info-card">
                            <h4>Insurance & Payment</h4>
                            <p>We accept most major insurance plans. Please verify your coverage before your visit and bring your insurance cards to your appointment. Payment is due at the time of service.</p>
                        </div>
                        <div className="info-card">
                            <h4>Medical Records</h4>
                            <p>If you have medical records from previous providers, please request them be sent to our office prior to your visit, or bring copies with you to your appointment.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewPatients; 